"use client";

import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Menu, MoveRight, X } from "lucide-react";
import Link from "next/link";
import { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { Logo } from "./logo";
import { ModeToggle } from "./mode-toggle";
import { LangToggle } from "./lang-toggle";
import { cn } from "@/lib/utils";
import { useTranslations, useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import { routing } from "@/i18n/routing";
import { getLocalizedHref } from "@/lib/site";

// ⬇️ NEW: Firestore client bits
import { db } from "@/lib/firebase.client"; // your provided firebase client init
import { doc, onSnapshot } from "firebase/firestore";
import { useAnyIdInView } from "@/hooks/use-any-id-in-view";
import { useAuth } from "@/components/providers/auth-provider";

// (optional) Type to make mapping clear
type FSMenuItem = { label: string; href: string; children?: FSMenuItem[] };

function Header() {
  const t = useTranslations("header");
  const locale = (useLocale() as "en" | "fr") ?? "en";
  const pathname = usePathname();
  const { user } = useAuth();

  // 🔹 Dynamic menu from Firestore
  const [headerMenu, setHeaderMenu] = useState<FSMenuItem[]>([]);
  const [isOpen, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const [rowH, setRowH] = useState(0);
  const [maintenance, setMaintenance] = useState(false);
  const hoverStateRef = useRef<Record<string, number>>({});

  const shouldSkipClick = (key: string) => {
    const ts = hoverStateRef.current[key];
    if (!ts) return false;
    const delta = Date.now() - ts;
    if (delta < 450) {
      hoverStateRef.current[key] = 0;
      return true;
    }
    return false;
  };

  useEffect(() => {
    const docId = process.env.NEXT_PUBLIC_WEBSITE_SETTINGS_DOC_ID || "default";
    const ref = doc(db, "website-settings", docId);

    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data();
      const items: FSMenuItem[] = data?.menus?.header?.[locale] ?? [];
      setMaintenance(data?.maintenance ?? false);
      setHeaderMenu(items);
    });

    return () => unsub();
  }, [locale]);

  const navigationItems = useMemo(() => {
    return (headerMenu || []).map((item) => {
      if (item.children && item.children.length > 0) {
        return {
          title: item.label,
          description: t("discover.description"),
          items: item.children.map((c) => ({
            title: c.label,
            href: getLocalizedHref(locale, c.href || "#"),
          })),
        };
      }
      return {
        title: item.label,
        href: getLocalizedHref(locale, item.href || "#"),
        description: t("discover.description"),
      };
    });
  }, [headerMenu, locale, t]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useLayoutEffect(() => {
    const measure = () =>
      setRowH(rowRef.current?.getBoundingClientRect().height ?? 0);
    measure();
    window.addEventListener("resize", measure, { passive: true });
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    const measure = () =>
      setRowH(rowRef.current?.getBoundingClientRect().height ?? 0);
    measure();
  }, [scrolled]);

  // If you want to still hide the logo based on intersections, keep your existing hook:
  const hideWhenVisibleIds = [
    "about-us-logo",
    "join-logo",
    "footer-logo",
    "hero-logo",
  ];
  const shouldHideLogo = useAnyIdInView(hideWhenVisibleIds, {
    rootMargin: "0px 0px -10% 0px",
    threshold: 0.2,
  });

  const accountPathConfig = routing.pathnames["/account"];
  const accountSegment =
    typeof accountPathConfig === "string"
      ? accountPathConfig
      : accountPathConfig?.[locale] ?? "/account";
  const accountPrefix = `/${locale}${accountSegment}`;
  const isAccountPage = pathname?.startsWith(accountPrefix);

  if (isAccountPage && user) {
    return null;
  }

  const homeHref = getLocalizedHref(locale, "/");
  const joinHref = getLocalizedHref(locale, "/#join");

  return (
    headerMenu.length > 0 &&
    !maintenance && (
      <header
        className={cn(
          "fixed inset-x-0 top-0 z-50 transition-all duration-300",
          scrolled
            ? "backdrop-blur-md bg-white/60 dark:bg-neutral-900/60 shadow-sm ring-1 ring-black/5 dark:ring-white/10 lg:top-5 lg:w-fit lg:mx-auto lg:rounded-2xl"
            : "backdrop-blur-xs bg-white/30 dark:bg-neutral-900/30 shadow-sm ring-1 ring-black/5 dark:ring-white/10 "
        )}
      >
        <div
          className={cn(
            "container mx-auto px-5 transition-all duration-300",
            scrolled ? "py-1 lg:rounded-2xl" : "py-1 lg:rounded-3xl"
          )}
        >
          <div
            ref={rowRef}
            className="relative flex h-fit flex-row items-center gap-4 lg:grid lg:grid-cols-3"
          >
            <div className="hidden flex-row items-center gap-4 lg:flex justify-start">
              <NavigationMenu className="flex items-start justify-start">
                <NavigationMenuList className="flex flex-row gap-4">
                  {navigationItems.map((item) => (
                    <NavigationMenuItem key={item.title}>
                      {"href" in item && item.href ? (
                        <NavigationMenuLink
                          href={item.href}
                          target={
                            item.href.startsWith("/") ? "_self" : "_blank"
                          }
                        >
                          <Button variant="ghost">{item.title}</Button>
                        </NavigationMenuLink>
                      ) : (
                        <>
                          <NavigationMenuTrigger
                            className="text-sm font-medium"
                            onPointerEnter={() => {
                              hoverStateRef.current[item.title] = Date.now();
                            }}
                            onClick={(event) => {
                              if (shouldSkipClick(item.title)) {
                                event.preventDefault();
                                event.stopPropagation();
                              }
                            }}
                          >
                            {item.title}
                          </NavigationMenuTrigger>
                          <NavigationMenuContent className="!w-[450px] p-4">
                            <div className="flex flex-col gap-4 lg:grid grid-cols-2">
                              <div className="flex h-full flex-col justify-between">
                                <div className="flex flex-col">
                                  <p className="text-base">{item.title}</p>
                                  {item.description ? (
                                    <p className="text-sm text-muted-foreground">
                                      {item.description}
                                    </p>
                                  ) : null}
                                </div>
                                <Link href={joinHref}>
                                  <Button size="sm" className="mt-10">
                                    {t("join-now")}
                                  </Button>
                                </Link>
                              </div>
                              <div className="flex h-full flex-col justify-end text-sm">
                                {("items" in item ? item.items ?? [] : []).map(
                                  (sub) => (
                                    <NavigationMenuLink
                                      href={sub.href}
                                      key={`${item.title}-${sub.title}`}
                                      target={
                                        sub.href.startsWith("/")
                                          ? "_self"
                                          : "_blank"
                                      }
                                      className="flex flex-row items-center justify-between rounded px-4 py-2 hover:bg-muted"
                                    >
                                      <span>{sub.title}</span>
                                      <MoveRight className="h-4 w-4 text-muted-foreground" />
                                    </NavigationMenuLink>
                                  )
                                )}
                              </div>
                            </div>
                          </NavigationMenuContent>
                        </>
                      )}
                    </NavigationMenuItem>
                  ))}
                </NavigationMenuList>
              </NavigationMenu>
            </div>

            <div
              className={cn(
                "origin-center transition-all duration-300 will-change-transform will-change-opacity flex justify-center",
                scrolled ? "scale-95" : "scale-100",
                shouldHideLogo
                  ? "opacity-0 scale-95 pointer-events-none select-none"
                  : "opacity-100 scale-100"
              )}
            >
              <Link
                className={cn(
                  "origin-center transition-transform duration-300",
                  scrolled ? "scale-95" : "scale-100"
                )}
                id="header-logo"
                href={homeHref}
              >
                <Logo />
              </Link>
            </div>

            <div className="flex w-full justify-end gap-4">
              <Link title="Login to your account" href={joinHref}>
                <Button variant="outline">{t("join-now")}</Button>
              </Link>
              <div className="hidden md:inline border-r" />
              <div className="hidden sm:flex gap-2">
                <ModeToggle />
                <LangToggle />
              </div>
            </div>

            <div className="ml-auto flex w-12 shrink items-end justify-end lg:hidden">
              <Button
                variant="ghost"
                aria-label={isOpen ? "Close menu" : "Open menu"}
                onClick={() => setOpen((v) => !v)}
              >
                {isOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>
        </div>

        {isOpen && (
          <div
            className={cn(
              "fixed inset-x-0 z-[60] lg:hidden border-t shadow-lg",
              scrolled ? "bg-background/95 backdrop-blur-md" : "bg-background"
            )}
            style={{ top: rowH }}
          >
            <div className="container mx-auto px-5 py-8 flex flex-col gap-8">
              {navigationItems.map((item) => (
                <div key={item.title} className="flex flex-col gap-2">
                  {"href" in item && item.href ? (
                    <Link
                      href={item.href}
                      className="flex items-center justify-between text-lg font-medium"
                      onClick={() => setOpen(false)}
                    >
                      <span>{item.title}</span>
                      <MoveRight className="h-4 w-4 stroke-1 text-muted-foreground" />
                    </Link>
                  ) : (
                    <>
                      <p className="text-lg font-semibold">{item.title}</p>
                      {("items" in item ? item.items ?? [] : []).map((sub) => (
                        <Link
                          key={`${item.title}-${sub.title}`}
                          href={sub.href}
                          className="ml-4 flex items-center justify-between text-sm text-muted-foreground hover:text-foreground"
                          onClick={() => setOpen(false)}
                        >
                          <span>{sub.title}</span>
                          <MoveRight className="h-3 w-3" />
                        </Link>
                      ))}
                    </>
                  )}
                </div>
              ))}
              <div className="mt-4 flex gap-2 border-t pt-4">
                <Link title="Login to your account" href={joinHref}>
                  <Button variant="outline" onClick={() => setOpen(false)}>
                    {t("join-now")}
                  </Button>
                </Link>
                <div className="sm:flex gap-2">
                  <ModeToggle />
                  <LangToggle />
                </div>
              </div>
            </div>
          </div>
        )}
      </header>
    )
  );
}

export { Header };
